import { getEvmNFTCollectionByAddress, returnChain } from '@repo/blockchain';
import { ChainType } from '@repo/types-client';

type Resource = string | { contractAddress: string };

/**
 * Parses a resource ID string into an object containing the contract address and ID.
 * @function parseResourceId
 * @param {string} context - The resource ID string to parse.
 * @returns {object | undefined} An object containing the contract address and ID, or undefined if the string is invalid.
 */
const parseResourceId = (context: string) => {
  const parsed = context.split(':');
  if (parsed.length < 2) return undefined;

  return {
    id: parsed[1],
    contractAddress: normalizeAddress(parsed[0] || ''),
  };
};

/**
 * Gets the contract address from a resource.
 * @function getResourceContractAddress
 * @param {Resource} context - The resource to get the contract address from.
 * @returns {string | undefined} The contract address, or undefined if not found.
 */
const getResourceContractAddress = (context: Resource) => {
  if (typeof context === 'string')
    return parseResourceId(context)?.contractAddress;
  return normalizeAddress(context?.contractAddress);
};

/**
 * Normalizes an Ethereum address to a lowercase string with a '0x' prefix.
 * @function normalizeAddress
 * @param {string} context - The address to normalize.
 * @returns {string} The normalized address.
 */
const normalizeAddress = (context: string) => {
  context = context || '';
  const prefix = context.startsWith('0x') ? '' : '0x';
  return `${prefix}${context}`.toLowerCase();
};

/**
 * Gets the EVM chain type for a resource.
 * @function getResourceEVMChain
 * @param {Resource} context - The resource to get the chain type for.
 * @returns {string | undefined} The EVM chain type, or undefined if not found.
 */
const getResourceEVMChain = (context: Resource) => {
  const contractAddress = getResourceContractAddress(context);
  const collection = getEvmNFTCollectionByAddress(
    contractAddress as `0x${string}`,
  );
  return collection?.chain;
};

/**
 * Gets the chain type for a resource.
 * @function getResourceChainType
 * @param {Resource} context - The resource to get the chain type for.
 * @returns {ChainType | undefined} The chain type, or undefined if not found.
 */
const getResourceChainType = (context: Resource): ChainType | undefined =>
  getResourceEVMChain(context) as ChainType;

/**
 * Gets the chain ID for a resource.
 * @function getResourceChainId
 * @param {Resource} context - The resource to get the chain ID for.
 * @returns {number | undefined} The chain ID, or undefined if not found.
 */
const getResourceChainId = (context: Resource) => {
  const chainType = getResourceEVMChain(context);
  if (!chainType) return undefined;
  return returnChain(chainType) || undefined;
};

export {
  parseResourceId,
  normalizeAddress,
  getResourceChainId,
  getResourceChainType,
};
